import React, { useState } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { formatDistanceToNow } from "date-fns"
import Tooltip from '@mui/material/Tooltip'

export interface Batch {
  id: number;
  name: string;
  createdAt: number;
  leadCount: number;
  acceptRate: number;
  replyRate: number;
  batchId: string;
}

interface ShortlistedLeadsSidebarProps {
  batches: Batch[];
  setBatchIDChild: (stype: string) => void;
}

const ShortlistedLeadsSidebar: React.FC<ShortlistedLeadsSidebarProps> = ({ batches, setBatchIDChild }) => {
  const [selectedBatch, setSelectedBatch] = useState<number | null>(null);

  return (
    <Box sx={{ 
      width: 380, 
      p: 2, 
      bgcolor: 'white', 
      border: '1px solid #e0e0e0',
      borderRadius: "8px",
      overflowY: 'auto',
      marginTop:"19%",
      height:"88%"
    }}>
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Shortlisted Leads</Typography>
        <Typography variant="body2" color="#325CD6" sx={{background:"#EAEFFF", borderRadius:"10px", fontSize:"12px" , fontWeight:"500", padding:"4px 8px" }}>{batches.length} Batches</Typography>
      </Box>
      
      <Divider sx={{ my: 1 }} />

      {batches.length === 0 ? (
        <Typography sx={{background: "#EAEEFB", color: "#5175E0", padding:"10px", textAlign:"center", borderRadius:"6px"}}>No Batches found</Typography>
      ) :
      (   
        batches.map((batch) => (
          <BatchCard 
          key={batch.id} 
          batch={batch} 
          isSelected={selectedBatch === batch.id}
          onClick={() => {
            setSelectedBatch(batch.id); 
            setBatchIDChild(batch.batchId);
          }}
          />
        ))
      )}
    </Box>
  );
};

interface BatchCardProps {
  batch: Batch;
  isSelected: boolean;
  onClick: () => void;
}

const PercentageCircle: React.FC<{ percentage: number; isSelected: boolean }> = ({ percentage, isSelected }) => {
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <Box sx={{ width: 20, height: 26, mr: 1 }}>
      <svg width="20" height="20" viewBox="0 0 20 20">
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke={isSelected ? '#e0e6ff' : '#f0f0f0'}
          strokeWidth="2"
        />
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke={isSelected ? '#325CD6' : '#757575'}
          strokeWidth="2"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 10 10)"
        />
      </svg>
    </Box>
  );
};

const BatchCard: React.FC<BatchCardProps> = ({ batch, isSelected, onClick }) => {
  return (
    
    <Paper 
      elevation={0} 
      onClick={onClick}
      sx={{ 
        mb: 2, 
        p: 2, 
        borderRadius: 2, 
        bgcolor: isSelected ? '#f0f4ff' : 'white',
        border: isSelected ? '1px solid #e0e6ff' : '1px solid #e0e0e0',
        cursor: 'pointer',
        '&:hover': {
          borderColor: isSelected ? '#e0e6ff' : '#325CD6'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ 
          bgcolor: isSelected ? '#e0e6ff' : '#f5f5f5', 
          borderRadius: 1, 
          width: 36, 
          height: 36, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mr: 1
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill={isSelected ? "#5175e0" : "#757575"}/>
          </svg>
        </Box>
        <Box>
          <Typography color={isSelected ? "#325CD6" : "text.primary"} fontSize="13px" fontWeight="600">
            <Tooltip title={batch.name} arrow>
              <span>{batch.name.length > 25 ? `${batch.name.substring(0, 25)}...` : batch.name}</span>
            </Tooltip>
          </Typography>
          <Typography fontSize="12px" color={isSelected ? "#325CD6" : "text.secondary"}>
            Leads: {batch.leadCount}
          </Typography>
        </Box>
        <Typography variant="caption" color={isSelected ? "#325CD6" : "text.secondary"} sx={{ ml: 'auto' }}>
         {formatDistanceToNow(new Date(batch.createdAt), { addSuffix: true })}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ 
          display: 'flex', 
          padding: "4px 8px", 
          alignItems: 'center', 
          background: isSelected ? "#fff" : "#f5f5f5", 
          border: isSelected ? "1px dashed #D1D5DB" : "1px solid #e0e0e0", 
          borderRadius: "8px" 
        }}>
          <PercentageCircle percentage={batch.acceptRate} isSelected={isSelected} />
          <Typography variant="caption" sx={{ ml: 1 }}>
            Accept rate: {batch.acceptRate}%
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          padding: "4px 8px", 
          alignItems: 'center', 
          background: isSelected ? "#fff" : "#f5f5f5", 
          border: isSelected ? "1px dashed #D1D5DB" : "1px solid #e0e0e0", 
          borderRadius: "8px" 
        }}>
          <PercentageCircle percentage={batch.replyRate} isSelected={isSelected} />
          <Typography variant="caption" sx={{ ml: 1 }}>
            Reply rate: {batch.replyRate}%
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default ShortlistedLeadsSidebar;
